<template>
  <div class="document-preview">
    <!-- PDF Viewer -->
    <div class="pdf-viewer">
      <div class="pdf-container" ref="pdfContainer">
        <!-- PDF.js Viewer (Full Features) -->
        <iframe
          v-if="pdfSource && !loading && !effectiveProcessingState"
          ref="pdfFrame"
          class="pdf-iframe"
          :src="advancedViewerUrl"
          @load="onAdvancedViewerLoaded"
          @error="onAdvancedViewerError"
        ></iframe>

        <!-- Processing State (when document is being processed) -->
        <div v-else-if="effectiveProcessingState" class="loading-state">
          <q-spinner size="50px" />
          <p class="processing-text">{{ effectiveProcessingMessage }}</p>
          <p class="processing-subtext">This may take a few minutes... Please wait until the document process reach 100%.</p>
        </div>

        <!-- Loading State (when document is being loaded) -->
        <div v-else-if="loading" class="loading-state">
          <q-spinner size="50px" />
          <p>Loading document...</p>
        </div>

        <!-- Error State -->
        <div v-else class="error-state">
          <q-icon name="error" size="50px" color="negative" />
          <p>Failed to load document</p>
          <q-btn flat @click="downloadFile">Download file instead</q-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getDocumentType } from '@/utils/fileUpload';
import { getDocumentFileUrl, getDocumentViewingUrl, isDocumentProcessing, getDocumentProcessingMessage } from '@/utils/documentProcessingUtils';

export default {
  name: 'DocumentPreview',
  props: {
    content: {
      type: Object,
      required: true
    },
    showToolbar: {
      type: Boolean,
      default: true
    },
    enableSearch: {
      type: Boolean,
      default: true
    },
    enableSidebar: {
      type: Boolean,
      default: true
    },
    documentProcessingStatuses: {
      type: Object,
      default: () => ({})
    },
    isProcessing: {
      type: Boolean,
      default: false
    },
    processingMessage: {
      type: String,
      default: 'Processing document...'
    }
  },
  data() {
    return {
      pdfSource: null,
      localPdfUrl: null, // Local blob URL for CORS-free viewing
      loading: true,
      error: false
    };
  },
  computed: {
    documentType() {
      return getDocumentType(this.content);
    },
    fileName() {
      return this.content.filename || this.content.name || 'Document';
    },
    fileSize() {
      if (this.content.size) {
        const bytes = this.content.size;
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
        return Math.round(bytes / 1048576) + ' MB';
      }
      return '';
    },
    fileUrl() {
      // PRIORITY 1: Check upload store for active document uploads with temporary URLs
      // This is where newly uploaded documents with processing status are tracked
      const uploads = this.$store?.getters?.['uploadProgress/uploads'] || [];
      const activeUpload = uploads.find(upload => upload.realId === this.content.id && upload.contentType === 'document');

      // Use temporary URL from active upload if available
      if (activeUpload?.processingStatusData?.optimized_url) {
        return activeUpload.processingStatusData.optimized_url;
      }

      // PRIORITY 2: Check documentProcessingStatuses prop (fallback for other flows)
      const processingStatusData = this.documentProcessingStatuses[this.content.id];

      // ENHANCED LOGIC: Check for temporary URLs from processing status API
      // When state reaches "process_document", the API provides temporary optimized_url for immediate viewing
      if (processingStatusData && processingStatusData.state === 'process_document' && processingStatusData.optimized_url) {
        return processingStatusData.optimized_url;
      }

      // FALLBACK: Also check for optimized_url when processing is completed
      if (processingStatusData && processingStatusData.processing_status === 'completed' && processingStatusData.optimized_url) {
        return processingStatusData.optimized_url;
      }

      // CRITICAL: Don't return any URL when document is still processing AND no temporary URLs available
      // This prevents loading corrupted/incomplete files
      const hasAnyTempUrl = activeUpload?.processingStatusData?.optimized_url || processingStatusData?.optimized_url;
      if (this.effectiveProcessingState && !hasAnyTempUrl) {
        return null;
      }

      // Use the viewing URL for PDF viewer (prioritizes PDF version for better browser compatibility)
      const url = getDocumentViewingUrl(this.content, this.documentProcessingStatuses);
      return url;
    },
    downloadUrl() {
      // Use the download URL for file downloads (always original document)
      return getDocumentFileUrl(this.content, this.documentProcessingStatuses);
    },
    advancedViewerUrl() {
      // Always try to show PDF viewer if we have a PDF source
      if (!this.localPdfUrl) return '';

      // Build PDF.js viewer URL with local blob URL to avoid CORS issues
      const baseUrl = '/pdfjs/web/viewer.html';
      const params = new URLSearchParams();

      // Use the local blob URL instead of the remote S3 URL
      params.append('file', this.localPdfUrl);

      // Advanced features from props
      if (!this.showToolbar) {
        params.append('toolbar', '0');
      }
      if (!this.enableSidebar) {
        params.append('sidebar', '0');
      }

      return `${baseUrl}?${params.toString()}`;
    },
    pageCount() {
      return this.content.extra_attributes?.page_count || null;
    },
    // Internal processing state detection using utility functions
    internalProcessingState() {
      // Get uploads from the store if available (for GalleryLightbox context)
      const uploads = this.$store?.getters?.['uploadProgress/uploads'] || [];
      return isDocumentProcessing(this.content.id, uploads, this.documentProcessingStatuses);
    },
    internalProcessingMessage() {
      // Get uploads from the store if available
      const uploads = this.$store?.getters?.['uploadProgress/uploads'] || [];
      return getDocumentProcessingMessage(this.content.id, uploads, this.documentProcessingStatuses);
    },
    // Use internal processing state if no external isProcessing prop provided
    effectiveProcessingState() {
      // If parent explicitly sets isProcessing, respect that
      // Otherwise, use internal detection
      return this.isProcessing !== false ? this.isProcessing : this.internalProcessingState;
    },
    effectiveProcessingMessage() {
      // If parent provides a message, use it; otherwise use internal message
      return this.processingMessage !== 'Processing document...' ? this.processingMessage : this.internalProcessingMessage;
    }
  },
  mounted() {
    this.loadDocument();
  },

  beforeDestroy() {
    // Clean up blob URL to prevent memory leaks
    if (this.localPdfUrl) {
      URL.revokeObjectURL(this.localPdfUrl);
      this.localPdfUrl = null;
    }
  },

  watch: {
    fileUrl() {
      // Reload PDF if URL changes
      this.loadDocument();
    },

    // Watch for content prop changes to handle version switching
    content: {
      handler(newContent, oldContent) {
        // Only reload if the content actually changed (different ID or URL)
        if (newContent && oldContent && (newContent.id !== oldContent.id || newContent.url !== oldContent.url || newContent.filename !== oldContent.filename)) {
          this.loadDocument();
        }
      },
      deep: true,
      immediate: false
    },

    // Watch for processing state changes
    effectiveProcessingState: {
      handler(newProcessing, oldProcessing) {
        // When processing completes (false), reload the document
        if (oldProcessing === true && newProcessing === false) {
          this.loadDocument();
        }
      },
      immediate: false
    }
  },

  methods: {
    async loadDocument() {
      // Check if we have a valid file URL (includes temporary URLs from processing status)
      if (!this.fileUrl) {
        this.loading = false;
        this.error = true;
        return;
      }

      // Always try to load documents as PDF (backend converts all documents to PDF)
      if (!this.fileUrl) {
        this.loading = false;
        this.error = true;
        return;
      }

      try {
        this.loading = true;
        this.error = false;

        // Fetch document from remote URL and create local blob URL to avoid CORS issues
        const response = await fetch(this.fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }

        const blob = await response.blob();

        // Clean up previous blob URL if it exists
        if (this.localPdfUrl) {
          URL.revokeObjectURL(this.localPdfUrl);
        }

        // Create local blob URL
        this.localPdfUrl = URL.createObjectURL(blob);
        this.pdfSource = this.localPdfUrl;
      } catch (error) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },

    onAdvancedViewerLoaded() {
      this.loading = false;
      this.error = false;
      this.$emit('viewer-loaded');
    },

    onAdvancedViewerError() {
      this.error = true;
      this.loading = false;
    },

    downloadFile() {
      if (this.downloadUrl) {
        const link = document.createElement('a');
        link.href = this.downloadUrl;
        link.download = this.fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    openInNewTab() {
      if (this.downloadUrl) {
        window.open(this.downloadUrl, '_blank');
      }
    },

    // Advanced PDF.js control methods (for external use if needed)
    async searchText(query) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.findController) {
          viewer.PDFViewerApplication.findController.executeCommand('find', {
            query,
            caseSensitive: false,
            entireWord: false,
            highlightAll: true
          });
        }
      }
    },

    setDarkMode(enabled) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.pdfViewer) {
          viewer.PDFViewerApplication.pdfViewer.pageColors = enabled
            ? {
                background: 'rgb(42, 46, 51)',
                foreground: 'rgb(249, 249, 250)'
              }
            : null;
        }
      }
    },

    zoomTo(scale) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.pdfViewer) {
          viewer.PDFViewerApplication.pdfViewer.currentScale = scale;
        }
      }
    },

    goToPage(pageNumber) {
      if (this.$refs.pdfFrame?.contentWindow) {
        const viewer = this.$refs.pdfFrame.contentWindow;
        if (viewer.PDFViewerApplication && viewer.PDFViewerApplication.pdfViewer) {
          viewer.PDFViewerApplication.pdfViewer.currentPageNumber = pageNumber;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.document-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-viewer {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.pdf-container {
  flex: 1;
  width: 100%;
  height: 100%;
  position: relative;
  background-color: #f5f5f5;
}

/* PDF.js iframe styling */
.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
  background-color: white;
  position: absolute;
  top: 0;
  left: 0;
}

.loading-state,
.error-state,
.processing-state,
.failed-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  color: var(--secondary-text-color);
  padding: 40px;
  height: 100%;

  .processing-text {
    @include mona-sans-font($size: 18px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
  }

  .processing-subtext {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin: 0;
    max-width: 300px;
    line-height: 1.4;
  }
}

.processing-info,
.failed-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.processing-info h4,
.failed-info h4 {
  @include mona-sans-font($size: 18px, $bolded: true, $color: var(--main-text-color));
  margin: 0;
}

.doc-name {
  @include inter-font($size: 16px, $bolded: true, $color: var(--main-text-color));
  margin: 0;
}

.doc-details {
  @include inter-font($size: 14px, $color: var(--secondary-text-color));
  margin: 0;
}
</style>
